import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import BrandName from './BrandName';
import StickyDemoFab from './StickyDemoFab';
import { DEMO_REQUEST_MAILTO } from '../config/contact';
const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Nav: logo routes home; primary nav keeps the live site focused on product, use cases, and proof.
  const menuItems = [
    { path: '/how-it-works', label: 'Platform', icon: <InfoIcon /> },
    { path: '/solution', label: 'Solutions', icon: <CheckCircleIcon /> },
    { path: '/resources', label: 'Insights', icon: <DescriptionIcon /> },
    { path: '/about', label: 'About', icon: <BusinessIcon /> },
  ];

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: 'var(--z-index-header)',
          height: 'var(--header-height)',
          backgroundColor: 'rgba(16,38,77,0.92)',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <Toolbar sx={{ height: '100%', minHeight: 'var(--header-height) !important', px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="default"
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer(true)}
            sx={{ mr: 2, display: { sm: 'none' }, color: 'white' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1.5 }}>
            <Box
              onClick={() => navigate('/')}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <BrandName
                useImage={true}
                logoVariant="wordmark"
                color="white"
                height={{ xs: 32, md: 36 }}
              />
            </Box>
          </Box>
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    borderRadius: '999px',
                    color: location.pathname === item.path ? 'white' : 'rgba(255,255,255,0.72)',
                    backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.12)' : 'transparent',
                    fontFamily: 'var(--font-sans)',
                    textTransform: 'none',
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    px: 1.75,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      color: 'white',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="contained"
                component="a"
                href={DEMO_REQUEST_MAILTO}
                sx={{
                  borderRadius: '999px',
                  backgroundColor: 'var(--brand-primary)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 400,
                  px: 2.25,
                  '&:hover': { backgroundColor: '#2d4a84' },
                }}
              >
                Request Demo
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          zIndex: 'var(--z-index-drawer)',
          '& .MuiDrawer-paper': {
            width: 'var(--drawer-width)',
            boxSizing: 'border-box',
            marginTop: 'var(--header-height)'
          },
        }}
      >
        <Box sx={{ overflow: 'auto', backgroundColor: 'white', height: '100%' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    mt: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(15,111,255,0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#10264d' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 400, color: '#10264d' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem disablePadding sx={{ px: 2, py: 2 }}>
              <Button
                variant="contained"
                fullWidth
                component="a"
                href={DEMO_REQUEST_MAILTO}
                sx={{
                  borderRadius: '999px',
                  backgroundColor: 'var(--brand-primary)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 400,
                  py: 1.5,
                  '&:hover': { backgroundColor: '#2d4a84' },
                }}
              >
                Request Demo
              </Button>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <StickyDemoFab />
    </>
  );
};

export default Header;
